import { useState } from "react";
import { useMutation, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { api, ApiError, type ApprovedDomain } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/overlay";
import { Input, Skeleton } from "../../components/ui/primitives";

// The Contacts page's "Approved domains" section: a contact's address must be at one of them. Every
// staff member sees the list; only super users add or remove domains. Changes land in the audit
// log's Contacts tab.

export function ApprovedDomains({ domains, superUser }: { domains: UseQueryResult<ApprovedDomain[]>; superUser: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [removing, setRemoving] = useState<string>();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["approved-domains"] });
    void qc.invalidateQueries({ queryKey: ["contact-events"] });
    void qc.invalidateQueries({ queryKey: ["schedule-recipients"] });
  };
  const add = useMutation({
    mutationFn: () => api.approveDomain(draft.trim()),
    onSuccess: () => {
      setDraft("");
      refresh();
    },
  });

  return (
    <section className="mb-5 rounded-[14px] border border-border bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-sm font-semibold">Approved domains</h2>
        <span className="text-xs text-muted">
          A contact's email must be at one of these.{!superUser && " A super user can add more."}
        </span>
      </div>
      {domains.isLoading ? (
        <Skeleton className="mt-3 h-7 w-64" />
      ) : domains.error ? (
        <p className="mt-3 text-sm text-bad">{domains.error.message}</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {!domains.data?.length && <span className="text-sm text-muted">None yet, so no one can add contacts.</span>}
          {domains.data?.map((d) => (
            <span key={d.domain} className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-surface-2 pl-2 pr-1 font-mono text-xs" title={`Approved ${new Date(d.addedAt).toLocaleDateString(undefined, { dateStyle: "medium" })}`}>
              {d.domain}
              {superUser && (
                <button onClick={() => setRemoving(d.domain)} aria-label={`Remove ${d.domain}`} className="rounded p-0.5 text-subtle hover:bg-surface hover:text-foreground">
                  <X className="size-3" />
                </button>
              )}
            </span>
          ))}
          {superUser && (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (draft.trim()) add.mutate();
              }}
            >
              <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="client.com" aria-label="Domain to approve" className="h-7 w-40 text-xs" />
              <Button type="submit" size="sm" disabled={!draft.trim() || add.isPending}><Plus className="size-3.5" /> Approve</Button>
            </form>
          )}
        </div>
      )}
      {add.error && <p className="mt-2 text-sm text-bad">{message(add.error)}</p>}
      {removing && <RemoveDomainDialog domain={removing} onDone={refresh} onClose={() => setRemoving(undefined)} />}
    </section>
  );
}

const message = (e: Error) => (e instanceof ApiError && e.status === 403 ? "Only super users can change approved domains." : e.message);

function RemoveDomainDialog({ domain, onDone, onClose }: { domain: string; onDone: () => void; onClose: () => void }) {
  const remove = useMutation({
    mutationFn: () => api.removeApprovedDomain(domain),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Remove approved domain" className="w-[440px] max-w-[calc(100vw-32px)] p-6">
      <h2 className="text-base font-semibold tracking-tight">Remove {domain}?</h2>
      <p className="mt-1 text-sm text-muted">
        Its contacts are kept, but each schedule drops them at its next send and emails its owner. Approving the domain again doesn't put
        them back: owners add them again, and resume any schedule that paused because no one was left.
      </p>
      {remove.error && <p className="mt-3 text-sm text-bad">{message(remove.error)}</p>}
      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="danger" disabled={remove.isPending} onClick={() => remove.mutate()}>Remove</Button>
      </div>
    </Dialog>
  );
}
